<script lang="ts">
	import { getCartIconContext } from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { DropPageGenerator } from '#lib/features/drop-page/components/DropPageGenerator/index.js';
	import { DropPageViewAfterOpenTracker } from '#lib/features/drop-page/components/DropPageViewAfterOpenTracker/index.js';
	import type { DropSections } from '#lib/features/drop-page/constants/dropSectionImports/types.js';
	import type { DropPageItem } from '#lib/features/drop-page/constants/types.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { EditionsMixpanelEvents } from '#lib/websites/editions/constants/mixpanel-events.js';
	import { page } from '$app/state';
	import type { EditionsPDPPageData } from '$routes/editions/[slug]/types';

	const { dropTypes } = getCartIconContext();

	let { liveDropInfoCtx } = getLiveDropInfoContext();

	// Get the codes of the editions shown on this drop page, returned in the associated server load function
	let editionsCodes = $derived(
		page.data.editionsCodes as EditionsPDPPageData['editionsCodes']
	);
	// Both loads parse the items' Markdown (`parseDropComponentsMarkdownServer`),
	// the REST one after its representation-only cast to the GraphQL item union
	// (rest-migration/feature-flags.md rule 4).
	let componentItems = $derived(page.data.componentItems as DropPageItem[]);
	// The components those items render, imported by the route's universal
	// load (`loadDropSections`).
	let dropSections = $derived(page.data.dropSections as DropSections);
	// Holds the series code or the first edition code (for seriesless editions)
	let registerInterestCode = $derived(
		page.data
			.registerInterestCode as EditionsPDPPageData['registerInterestCode']
	);
	let pastDropType = $derived(
		page.data.pastDropType as EditionsPDPPageData['pastDropType']
	);
	let registerInterestContentKey = $derived(
		page.data
			.registerInterestContentKey as EditionsPDPPageData['registerInterestContentKey']
	);

	// Becomes truthy when the last drop is loaded in the EditionsHeader component
	let showCardButtons = $derived(page.data.pastDropPage || !!$dropTypes);

	// True when at least one edition available for purchase is shown on this Edition drop page
	// (We currently don't support drops when not every edition is available for purchase/application)
	let showCardCartButton = $derived(
		!pastDropType &&
			editionsCodes.some(
				(code) => liveDropInfoCtx.liveDropInfo?.editions[code]?.isLive
			)
	);

	let showRegisterCode = $derived(
		(() => {
			// If the show_register_interest is disabled for this drop page
			// Then the component should not be shown
			if (!registerInterestCode) {
				return null;
			}

			// If the editions open for purchase/applications are not loaded yet
			// Or if the drop is not open for purchase/applications
			// Then we can show the show_register_interest component
			if (!!pastDropType || (!!$dropTypes && !showCardCartButton)) {
				return registerInterestCode;
			}

			// Otherwise, we don't show it
			return null;
		})()
	);
</script>

<DropPageViewAfterOpenTracker
	event={EditionsMixpanelEvents.DropPageViewAfterOpen}
	dropConfigId={page.data.dropConfigId}
	slug={page.params.slug || ''}
	dropStartDate={page.data.dropStartDate}
	isLive={showCardCartButton}
/>

<!-- Renders the drop page -->
<DropPageGenerator
	dropConfigId={page.data.dropConfigId}
	productId={page.data.productId}
	{showRegisterCode}
	registerInterestContentKey={registerInterestContentKey ?? undefined}
	{componentItems}
	sections={dropSections}
	{showCardButtons}
	{showCardCartButton}
	{pastDropType}
/>
